/**
 * PostHog Analytics Initialization Wrapper
 * Observability & Analytics Infrastructure - Task 4.3
 *
 * Initializes PostHog with EU region, PII scrubbing, and feature flag context.
 * Exports: initPostHog(), trackEvent(), identifyUser()
 */

import { getFrontendFeatureFlags } from "@vardic/shared/utils/feature-flag-context";

import { isFeatureEnabled } from "./feature-flags";
import { scrubPII } from "@vardic/shared/utils/pii-scrubbing";

type PostHog = import("posthog-js").PostHog;

let isInitialized = false;
let instance: PostHog | null = null;
let posthogModulePromise: Promise<typeof import("posthog-js")> | null = null;

function loadPostHogModule(): Promise<typeof import("posthog-js")> {
  if (!posthogModulePromise) {
    posthogModulePromise = import("posthog-js");
  }
  return posthogModulePromise;
}

/**
 * Initialize PostHog with EU region and GDPR-compliant configuration
 * Call this once at app startup (in main.tsx)
 *
 * Configuration:
 * - EU region (host from VITE_POSTHOG_HOST)
 * - localStorage persistence (GDPR-friendly)
 * - PII scrubbing via property_denylist
 * - Feature flags disabled (using our own FF system)
 * - No autocapture or session recording
 */
export async function initPostHog(): Promise<void> {
  // Skip if already initialized
  if (isInitialized) {
    return;
  }

  const envApiKey: unknown = import.meta.env.VITE_POSTHOG_KEY;
  const envApiHost: unknown = import.meta.env.VITE_POSTHOG_HOST;
  const apiKey = typeof envApiKey === "string" ? envApiKey : "";
  const apiHost = typeof envApiHost === "string" && envApiHost.length > 0
    ? envApiHost
    : "https://eu.i.posthog.com";

  // Graceful degradation: skip if API key missing
  if (!apiKey) {
    console.warn("PostHog API key not configured, analytics disabled");
    return;
  }

  try {
    const { default: posthog } = await loadPostHogModule();
    instance = posthog.init(apiKey, {
      api_host: apiHost,

      // GDPR-compliant persistence
      persistence: "localStorage",

      // Disable automatic features (manual tracking only)
      autocapture: false,
      capture_pageview: false,
      disable_session_recording: true,

      // PII scrubbing: deny list for common PII fields
      // PostHog automatically scrubs properties matching these names
      property_denylist: [
        // User info
        "email",
        "name",
        "firstName",
        "lastName",
        "fullName",
        "userName",

        // Location
        "$ip",
        "$current_url", // May contain sensitive query params
        "$initial_referrer",
        "$initial_referring_domain",

        // Device info (beyond what we need)
        "$device_id",
        "$user_id", // Use our own userId instead

        // Payment info
        "cardNumber",
        "card_number",
        "billing_address",
        "vat_id",
      ],

      // Don't use PostHog feature flags (we have our own system)
      bootstrap: {
        featureFlags: {},
      },

      // pirx-p953i: gate PostHog Support / Conversations at SDK init.
      // posthog-js exposes PostHogConfig.disable_conversations (default false);
      // we invert our mirrored flag so that, when the flag is OFF (default),
      // the SDK itself disables conversations ingestion. No channel is
      // connected yet, so this only documents the gate at SDK init time.
      disable_conversations: !isFeatureEnabled("POSTHOG_CONVERSATIONS"),
    });

    isInitialized = true;
  } catch (error) {
    console.error("PostHog initialization failed:", error);
    // Graceful degradation: continue without analytics
  }
}

/**
 * Track a custom event with automatic context injection
 *
 * Automatically attaches:
 * - User ID (if set via identifyUser)
 * - Feature flags (from feature-flag-context.ts)
 * - Timestamp
 *
 * PII is scrubbed before sending (via scrubPII utility)
 *
 * @param eventName Event name (e.g., "upload_started")
 * @param properties Optional event properties
 */
export async function trackEvent(
  eventName: string,
  properties: Record<string, unknown> = {},
): Promise<void> {
  if (!isInitialized || !instance) {
    // Analytics not enabled, skip silently
    return;
  }

  try {
    // Get feature flags
    const featureFlagContext = await getFrontendFeatureFlags();

    // Merge properties with context
    const eventProperties = {
      ...properties,
      featureFlags: featureFlagContext.flags,
      timestamp: Date.now(),
    };

    // Scrub PII from properties
    const scrubbedProperties = scrubPII(eventProperties) as Record<
      string,
      unknown
    >;

    // Send to PostHog
    instance.capture(eventName, scrubbedProperties);
  } catch (error) {
    // Non-blocking: log error but don't crash
    console.error("PostHog failed to track event:", eventName, error);
  }
}

/**
 * Identify a user for analytics tracking
 *
 * Call this on login to associate events with a user ID.
 * Call with null on logout to reset the user identity.
 *
 * @param userId User ID (hashed/anonymized) or null to reset
 */
export async function identifyUser(
  userId: string | null,
  profile?: {
    signupDate?: string;
    hasTeam?: boolean;
    locale?: string;
    plan?: string;
  },
): Promise<void> {
  if (!isInitialized || !instance) return;
  try {
    if (userId) {
      // Person properties only — featureFlags is per-event context, not a
      // person attribute (trackEvent already attaches it to events below).
      // identify()'s userPropertiesToSet is the modern replacement for the
      // deprecated instance.people.set() API; calling both was a double
      // write of the same properties.
      const personProperties: Record<string, unknown> = {};
      if (profile?.signupDate) {
        personProperties.signup_date = profile.signupDate;
      }
      if (typeof profile?.hasTeam === "boolean") {
        personProperties.has_team = profile.hasTeam;
      }
      if (profile?.locale) personProperties.locale = profile.locale;
      if (profile?.plan) personProperties.plan = profile.plan;
      instance.identify(userId, personProperties);
    } else {
      instance.reset();
    }
  } catch (error) {
    console.error("PostHog failed to identify user:", error);
  }
}

/**
 * Get PostHog instance (for advanced usage)
 * Returns null if not initialized
 */
export function getPostHogInstance(): PostHog | null {
  return instance;
}
